import { useEffect, useState } from "react";
import { Crown, LayoutDashboard, DoorOpen } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../store/AuthContext";
import { useWorkspace } from "../../store/WorkspaceContext";

const SEEN_KEY = "xpattern.adminSeenAt";

export const AccountMenu = () => {
  const { user, setPreviewAs } = useAuth();
  const { state, dispatch } = useWorkspace();
  const [, setNewCount] = useState(0);
  const isAdmin = user?.role === "admin";
  const [openRequests, setOpenRequests] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    const check = () => {
      api.adminCustomers().then((rows) => {
        const seen = localStorage.getItem(SEEN_KEY) || "1970-01-01";
        setNewCount(rows.filter((c) => c.created_at && c.created_at > seen).length);
      }).catch(() => {});
      api.adminRequests().then((rows) => setOpenRequests(rows.filter((r) => r.status === "open").length)).catch(() => {});
    };
    check();
    const t = setInterval(() => { if (document.visibilityState === "visible") check(); }, 60000);
    window.addEventListener("xp:requests-changed", check);
    return () => { clearInterval(t); window.removeEventListener("xp:requests-changed", check); };
  }, [isAdmin]);
  useEffect(() => {
    if (!isAdmin || state.dialog !== "admin") return;
    sessionStorage.setItem("xpattern.adminSeenPrev", localStorage.getItem(SEEN_KEY) || "1970-01-01");
    localStorage.setItem(SEEN_KEY, new Date().toISOString()); setNewCount(0);
  }, [isAdmin, state.dialog]);
  if (!user) return null;
  const label = user.role === "admin" ? "Owner" : user.name || user.shop_name || user.email;
  const initial = label.trim()[0]?.toUpperCase();
  if (!isAdmin) {
    // Customers: the name button opens My Dashboard directly (no drop-down)
    return (
      <button data-testid="account-menu-btn" title="My Dashboard — plan, plotters, requests" onClick={() => dispatch({ type: "SET", patch: { dialog: "profile" } })} className="relative ml-1 flex items-center gap-2 rounded-md border border-[#27324a] bg-[#0c1017] px-2 py-1 text-[12px] transition-colors hover:bg-[#1a2231]">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2f80ff] text-[11px] font-semibold text-white">{initial}</span>
        <span className="hidden max-w-[120px] truncate 2xl:inline" data-testid="account-label">My Dashboard</span>
        <LayoutDashboard className="h-3.5 w-3.5 text-[#9aa0a8]" />
      </button>
    );
  }
  // Owner: a door icon (same glossy style as show/hide) opens the customer preview; everything else lives in the Owner dashboard.
  return (
    <button data-testid="preview-customer-btn" title="View as a customer — free trial & subscription screens" onClick={() => setPreviewAs("trial")} className="glossy-icon glossy-dim relative ml-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
      <DoorOpen className="h-3 w-3" />
      {openRequests > 0 && <span data-testid="requests-badge" title={`${openRequests} vehicle request${openRequests === 1 ? "" : "s"} waiting`} className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#e5484d] px-1 text-[10px] font-bold leading-none text-white shadow">{openRequests > 99 ? "99+" : openRequests}</span>}
    </button>
  );
};

export const TrialBanner = () => {
  const { user } = useAuth();
  const { dispatch } = useWorkspace();
  if (!user || user.entitled) return null;
  const renewal = user.renewal_required;
  return (
    <div className="flex items-center gap-3 border-b border-[#2f80ff]/40 bg-[#2f80ff]/15 px-3 py-1.5 text-[12px] text-[#e8e9ec]" data-testid="trial-ended-banner" data-locked={Boolean(user.trial_locked)}>
      <Crown className="h-4 w-4 text-[#2f80ff]" />
      <span className="flex-1">{user.demo_ended && user.suspended ? "Your Xpattern demo has ended — thanks for trying it! Pick a plan to keep cutting on this same account (nothing to reinstall)." : user.demo ? "Your Xpattern demo has ended — thanks for trying it! Contact support@xpattern.net to keep cutting." : user.suspended ? "This account has been deactivated by Xpattern. Contact support@xpattern.net to restore access." : user.trial_locked ? user.trial_locked : renewal ? (user.plan_state === "past_due" ? "Your automatic payment failed — renewal required to keep cutting." : "Your month of Xpattern Pro has ended — renewal required to keep cutting.") : `Your ${user.trial_days || 7}-day free trial has ended. You can still browse and lay out patterns — subscribe to cut again.`}</span>
      {((!user.suspended && !user.demo) || (user.demo_ended && user.suspended)) && <button data-testid="trial-banner-subscribe-btn" onClick={() => dispatch({ type: "SET", patch: { dialog: "subscribe" } })} className="btn-red py-1">{user.demo_ended ? "Subscribe" : user.trial_locked ? "Purchase license" : renewal ? "Renew" : "Subscribe"}</button>}
    </div>
  );
};
